// src/utils/doppioniMateriali.js
// ═══════════════════════════════════════════════════════════════════════════
// CONTROLLO DEI MATERIALI DOPPI SULLA STESSA COMMESSA (ottobre 2026, W408)
// ═══════════════════════════════════════════════════════════════════════════
// Un materiale può entrare in una commessa da più strade:
//   • ODA dell'ufficio acquisti (ordiniAcquisto, righe);
//   • NF, richiesta di nuova fornitura all'ufficio acquisti (nuoveForniture, articoli);
//   • richiesta al magazzino (richiesteMateriali, righe);
//   • voce scritta a mano in Controllo Economico (movimentiEconomici, categoria «materiali»).
// Se due persone mettono la stessa cosa (il PM e l'ufficio acquisti, due ODA…), il costo o
// l'ordine si raddoppia. Qui si confrontano le voci: articolo (codice, se c'è), descrizione
// normalizzata con le misure che devono coincidere, quantità e data vicina.
// Nexiora SEGNALA soltanto: decide sempre una persona («È la stessa cosa?»), e la risposta
// «No, è un'altra cosa» resta scritta, così non la richiede più.
// Funzioni pure, senza Firestore: prove in demo/prove/prova_doppioni.mjs.
// ═══════════════════════════════════════════════════════════════════════════

export const GIORNI_FINESTRA = 90; // due voci uguali a più di 3 mesi di distanza: acquisti diversi

const PAROLE_VUOTE = new Set(["di", "da", "del", "della", "dei", "delle", "per", "con", "in", "il", "lo", "la", "le", "gli", "un", "una", "uno",
  "ed", "al", "alla", "ai", "mt", "metri", "metro", "pz", "pezzi", "pezzo", "nr", "cad", "tipo", "art", "articolo", "cod", "codice", "conf", "confezione"]);

/** Testo confrontabile: minuscolo, senza accenti, misure scritte allo stesso modo (3G2,5 = 3x2.5). */
export function normalizza(testo) {
  return String(testo || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/×/g, "x").replace(/²/g, "q")
    .replace(/(\d),(\d)/g, "$1.$2")
    .replace(/(\d)\s*[gx]\s*(\d)/g, "$1x$2")
    .replace(/(\d)\s+(mmq|mm2|mm|cm|m|a|v|w|kw|kva|ma)\b/g, "$1$2")
    .replace(/mm2\b/g, "mmq");
}

/** Parole e misure di una descrizione. Misure = solo numeri (3x2.5, 2.5, 100); le sigle con
 *  lettere e numeri (fg16or16, 63a) contano come parole, confrontate anche per l'inizio. */
export function parti(testo) {
  const token = normalizza(testo).split(/[^a-z0-9.]+/).map(t => t.replace(/^\.+|\.+$/g, "")).filter(Boolean);
  const misure = new Set(), parole = new Set();
  token.forEach(t => {
    if (/^[\d.x]+$/.test(t)) { if (/\d/.test(t)) misure.add(t); }
    else if (!PAROLE_VUOTE.has(t) && (t.length >= 2 || /\d/.test(t))) parole.add(t);
  });
  return { misure, parole };
}

const stessaParola = (a, b) => a === b || (a.length >= 4 && b.length >= 4 && (a.startsWith(b) || b.startsWith(a)));

/** Somiglianza tra due insiemi di parole (0..1): parole in comune / parole in tutto. */
export function somiglianza(pa, pb) {
  const A = [...pa], B = [...pb];
  if (!A.length || !B.length) return 0;
  const usate = new Set();
  let comuni = 0;
  A.forEach(a => { const j = B.findIndex((b, i) => !usate.has(i) && stessaParola(a, b)); if (j >= 0) { usate.add(j); comuni++; } });
  return comuni / (A.length + B.length - comuni);
}

const stessoInsieme = (a, b) => a.size === b.size && [...a].every(x => b.has(x));
const codiceNorm = (c) => String(c || "").toLowerCase().replace(/[^a-z0-9]/g, "");
/** «1.234,5» → 1234.5 · «2.5» → 2.5 · «» → null */
export const numero = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  let s = String(v ?? "").trim();
  if (!s) return null;
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
};
const giorni = (a, b) => (a && b ? Math.abs((Date.parse(a + "T00:00:00") - Date.parse(b + "T00:00:00")) / 86400000) : 0);

/**
 * Confronto di due voci. null = diverse; altrimenti { livello: "forte"|"possibile", motivo }.
 * Voce: { descrizione, codice, quantita, data (AAAA-MM-GG) }.
 */
export function confronta(a, b, { finestra = GIORNI_FINESTRA } = {}) {
  if (!a || !b) return null;
  if (giorni(a.data, b.data) > finestra) return null;
  const ca = codiceNorm(a.codice), cb = codiceNorm(b.codice);
  const qa = numero(a.quantita), qb = numero(b.quantita);
  const stessaQ = qa !== null && qb !== null && Math.abs(qa - qb) < 1e-9;
  if (ca && cb && ca === cb) return { livello: "forte", motivo: "stesso codice articolo" };
  // Codici diversi non bastano a dire «articoli diversi»: il codice del magazzino e quello del
  // fornitore spesso non coincidono. Si guarda la descrizione, ma al massimo «possibile».
  const codiciDiversi = !!(ca && cb);
  const A = parti(a.descrizione), B = parti(b.descrizione);
  if (!stessoInsieme(A.misure, B.misure)) return null;                      // 3x2.5 non è 3x1.5
  const s = somiglianza(A.parole, B.parole);
  const pochi = A.parole.size + A.misure.size < 2 || B.parole.size + B.misure.size < 2;
  if (pochi) return s === 1 && stessaQ ? { livello: "possibile", motivo: "stessa descrizione e quantità" } : null;
  if (s >= 0.75) return stessaQ && !codiciDiversi ? { livello: "forte", motivo: "stessa descrizione e quantità" }
    : { livello: "possibile", motivo: codiciDiversi ? "descrizione uguale, codici diversi" : "descrizione uguale o quasi" };
  if (codiciDiversi) return null;
  if (s >= 0.5 && A.misure.size > 0) return { livello: "possibile", motivo: "descrizione simile, stesse misure" };
  return null;
}

// ─── Voci della commessa, da tutte le strade ──────────────────────────────
const isoDi = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10);
  const d = v.toDate ? v.toDate() : v.seconds ? new Date(v.seconds * 1000) : v instanceof Date ? v : null;
  return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";
};
const STATI_ODA_FUORI = ["Annullato"];

/**
 * Tutte le voci di materiale di una commessa. Esclude ciò che non è un acquisto vivo
 * (ODA annullati o eliminati, NF in bozza o già diventate ODA, richieste annullate).
 */
export function vociMaterialiCommessa({ oda = [], nf = [], richieste = [], movimenti = [] }) {
  const voci = [];
  const aggiungi = (doc, fonte, etichetta, righe, base) => righe.forEach((r, i) => {
    if (!String(r?.descrizione || "").trim()) return;
    voci.push({ chiave: `${fonte}:${doc.id}:${r.id || i}`, doc: `${fonte}:${doc.id}`, fonte, etichetta,
      descrizione: String(r.descrizione).trim(), codice: r.codice || "", quantita: r.quantita ?? "", um: r.unitaMisura || r.um || "",
      prezzo: r.prezzoUnitario ?? r.prezzoIndicativo ?? "", nonDoppioni: doc.nonDoppioni || [], ...base });
  });
  oda.filter(o => !o.eliminato && !STATI_ODA_FUORI.includes(o.stato)).forEach(o =>
    aggiungi(o, "oda", `ODA ${o.numeroODA || o.numeroOda || ""}`.trim(), o.righe || [], { data: o.data || isoDi(o.createdAt), chi: o.creatoDaNome || o.createdByName || "Ufficio acquisti", stato: o.stato || "" }));
  nf.filter(n => !(n.odaId && n.odaStato !== "Annullato") && (n.statoRichiesta || "Inviata") !== "Da inviare" && !n.annullata && !n.eliminata).forEach(n =>
    aggiungi(n, "nf", `NF ${n.numero || ""}`.trim(), n.articoli || [], { data: isoDi(n.inviataAt || n.createdAt), chi: n.compilatoreNome || "", stato: n.statoRichiesta || "" }));
  richieste.filter(r => r.stato !== "annullata" && r.statoRichiesta !== "Da inviare").forEach(r =>
    aggiungi(r, "mag", "Richiesta al magazzino", r.righe || [], { data: isoDi(r.createdAt), chi: r.richiestaFromName || "", stato: r.stato || "" }));
  // voci a mano: non quelle che il magazzino scrive evadendo una richiesta (stessa strada della
  // richiesta) né i rientri dal cantiere (quantità in meno, non un acquisto)
  movimenti.filter(m => m.categoriaId === "materiali" && m.dati && !m.richiestaMaterialeId && !m.origineRientroId && !((numero(m.dati.quantita) ?? 0) < 0)).forEach(m =>
    aggiungi(m, "man", "Voce a mano in Controllo Economico", [{ ...m.dati, id: "0" }], { data: m.dati.data || isoDi(m.creadaAt), chi: m.creadaDaNome || "", stato: "" }));
  return voci;
}

/** La coppia è già stata giudicata «non è un doppione»? */
export const chiaveCoppia = (a, b) => [a, b].sort().join("|");
const escluso = (a, b, esclusi) => a.nonDoppioni.includes(b.chiave) || a.nonDoppioni.includes(b.doc) || b.nonDoppioni.includes(a.chiave) || b.nonDoppioni.includes(a.doc)
  || (esclusi && (esclusi.has ? esclusi.has(chiaveCoppia(a.chiave, b.chiave)) : esclusi[chiaveCoppia(a.chiave, b.chiave)]));

/**
 * Prima di inserire: le righe nuove somigliano a qualcosa che c'è già?
 * righe: [{ descrizione, codice, quantita }] · data: giorno dell'inserimento · escludiDoc: il documento che si sta
 * modificando («oda:ID») · nonDoppioni: le voci che quel documento ha già giudicato diverse.
 * → [{ riga, voce, livello, motivo }], prima i «forte».
 */
export function cercaDoppioni(righe, voci, { data, escludiDoc = null, nonDoppioni = [] } = {}) {
  const trovati = [];
  const giaGiudicata = (voce) => nonDoppioni.includes(voce.chiave) || nonDoppioni.includes(voce.doc)
    || (escludiDoc && voce.nonDoppioni.some(k => k === escludiDoc || k.startsWith(escludiDoc + ":")));
  (righe || []).filter(r => String(r?.descrizione || "").trim()).forEach(riga => {
    voci.forEach(voce => {
      if ((escludiDoc && voce.doc === escludiDoc) || giaGiudicata(voce)) return;
      const esito = confronta({ ...riga, data: riga.data || data }, voce);
      if (esito) trovati.push({ riga, voce, ...esito });
    });
  });
  return trovati.sort((x, y) => (x.livello === y.livello ? 0 : x.livello === "forte" ? -1 : 1));
}

/** Tutte le coppie sospette già presenti nella commessa (riquadro in Controllo Economico). */
export function coppieDoppioni(voci, esclusi = null) {
  const coppie = [];
  for (let i = 0; i < voci.length; i++) for (let j = i + 1; j < voci.length; j++) {
    const a = voci[i], b = voci[j];
    if (a.doc === b.doc || escluso(a, b, esclusi)) continue;
    const esito = confronta(a, b);
    if (esito) coppie.push({ a, b, chiave: chiaveCoppia(a.chiave, b.chiave), ...esito });
  }
  return coppie.sort((x, y) => (x.livello === y.livello ? 0 : x.livello === "forte" ? -1 : 1));
}

/** «100 m · € 1,20» per mostrare una voce in breve (senza prezzo per chi non vede gli importi). */
export function dettaglioVoce(v, { prezzo: conPrezzo = true } = {}) {
  const q = v.quantita !== "" && v.quantita !== null && v.quantita !== undefined ? `${v.quantita}${v.um ? " " + v.um : ""}` : "";
  const p = conPrezzo && numero(v.prezzo) ? `€ ${numero(v.prezzo).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
  return [q, p].filter(Boolean).join(" · ");
}
